import { useState, useEffect, useMemo, useCallback } from "react";
import { useRoleGuard } from "@/hooks/useRoleGuard";
import { settingsApi } from "@/services/api";

export const PERMISSIONS_STORAGE_KEY = "erp_role_permissions";

export type RolePermissionsMap = Record<string, Record<string, string[]>>;

// In-memory cache so we don't re-fetch on every component mount
let _permCache: RolePermissionsMap | null = null;
let _permFetchPromise: Promise<RolePermissionsMap> | null = null;

async function fetchPermissions(): Promise<RolePermissionsMap> {
  if (_permCache !== null) return _permCache;
  if (_permFetchPromise) return _permFetchPromise;

  _permFetchPromise = settingsApi.getRolePermissions()
    .then((data: { permissions?: RolePermissionsMap } | RolePermissionsMap) => {
      const perms = (data as { permissions?: RolePermissionsMap }).permissions ?? (data as RolePermissionsMap) ?? {};
      _permCache = perms;
      return perms;
    })
    .catch(() => {
      _permCache = {};
      return {} as RolePermissionsMap;
    })
    .finally(() => { _permFetchPromise = null; });

  return _permFetchPromise;
}

/** Invalidate in-memory cache (call after saving permissions) */
export function invalidatePermissionsCache() {
  _permCache = null;
  _permFetchPromise = null;
}

/** Save role permissions to DB */
export async function savePermissions(map: RolePermissionsMap): Promise<void> {
  await settingsApi.saveRolePermissions(map);
  _permCache = map; // update in-memory cache immediately
  window.dispatchEvent(new CustomEvent("erp-permissions-updated", { detail: map }));
}

/** Read permissions from in-memory cache (sync, for sidebar filtering) */
export function getStoredPermissions(): RolePermissionsMap {
  return _permCache ?? {};
}

/**
 * usePagePermission — checks whether the current user's role has a specific
 * permission on a specific module, fetched from the database.
 *
 * Falls back to `defaultAccess` when no saved permissions exist for the role.
 */
export function usePagePermission(
  module: string,
  permission: "view" | "create" | "edit" | "delete" | "export" | "upload" | "approve" = "view",
  defaultAccess = true
): { hasPermission: boolean; user: ReturnType<typeof useRoleGuard>["user"]; loading: boolean } {
  const { user, loading: authLoading } = useRoleGuard([]);
  const [perms, setPerms] = useState<RolePermissionsMap>(_permCache ?? {});
  const [permsLoading, setPermsLoading] = useState(_permCache === null);

  const loadPerms = useCallback(async () => {
    setPermsLoading(true);
    const result = await fetchPermissions();
    setPerms(result);
    setPermsLoading(false);
  }, []);

  useEffect(() => {
    loadPerms();
  }, [loadPerms]);

  // Re-check when admin saves new permissions
  useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<RolePermissionsMap>;
      setPerms(custom.detail ?? {});
    };
    window.addEventListener("erp-permissions-updated", handler);
    return () => window.removeEventListener("erp-permissions-updated", handler);
  }, []);

  const hasPermission = useMemo(() => {
    if (!user) return false;

    const roleKey = Object.keys(perms).find(
      (k) => k.trim().toLowerCase() === (user.role || "").trim().toLowerCase()
    );

    // No saved permissions for this role → use the default
    if (!roleKey) return defaultAccess;

    const modulePerms = perms[roleKey]?.[module] ?? [];
    return modulePerms.includes(permission);
  }, [user, perms, module, permission, defaultAccess]);

  return { hasPermission, user, loading: authLoading || permsLoading };
}
